import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Group,
  Stack,
  Text,
} from '@mantine/core'
import { IconRefresh } from '@/components/icons'
import { showNotification } from '@mantine/notifications'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
} from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { DebugShiftBreakIssueRow, DebugShiftBreakRepairResponse } from '@/types/api'

const ISSUE_LABELS: Record<string, string> = {
  closed_shift_orphan_break: 'Перерыв не закрыт на завершённой смене',
  open_shift_over_limit: 'Перерыв превышает суточный лимит',
  break_before_shift: 'Перерыв начался до начала смены',
}

function userLabel(row: DebugShiftBreakIssueRow): string {
  if (row.user_name && row.user_email) return `${row.user_name} (${row.user_email})`
  return row.user_name || row.user_email || row.user_id
}

export function DebugShiftBreaksTab() {
  const [rows, setRows] = useState<DebugShiftBreakIssueRow[]>([])
  const [loading, setLoading] = useState(true)
  const [repairing, setRepairing] = useState(false)
  const [repairingId, setRepairingId] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<DebugShiftBreakRepairResponse | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.debugScanShiftBreaks()
      setRows(res.issues || [])
    } catch (e) {
      showNotification({
        color: 'red',
        title: 'Перерывы смен',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить список',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const runRepair = useCallback(
    async (opts: { dryRun: boolean; shiftLogIds?: string[] }) => {
      const singleId = opts.shiftLogIds?.length === 1 ? opts.shiftLogIds[0] : null
      if (singleId) setRepairingId(singleId)
      else setRepairing(true)
      try {
        const res = await api.debugRepairShiftBreaks({
          dry_run: opts.dryRun,
          shift_log_ids: opts.shiftLogIds,
        })
        setLastResult(res)
        setRows(res.remaining_issues || [])
        showNotification({
          color: opts.dryRun ? 'blue' : 'green',
          title: opts.dryRun ? 'Проверка' : 'Исправление',
          message: opts.dryRun
            ? `Будет исправлено: ${res.fixed_closed + res.fixed_open} (закрытые: ${res.fixed_closed}, открытые: ${res.fixed_open})`
            : `Исправлено: ${res.fixed_closed + res.fixed_open}. Осталось проблем: ${res.remaining_total}`,
        })
      } catch (e) {
        showNotification({
          color: 'red',
          title: opts.dryRun ? 'Проверка' : 'Исправление',
          message: e instanceof ApiError ? e.message : 'Операция не выполнена',
        })
      } finally {
        setRepairing(false)
        setRepairingId(null)
      }
    },
    [],
  )

  return (
    <Stack gap="sm">
      <Alert color="blue" variant="light" title="Зависшие перерывы в сменах">
        Инструмент для администратора: закрывает «зависший» перерыв, из‑за которого оператор не
        может взять новый перерыв, а в табеле не отображаются интервалы. Действует только в вашем
        workspace.
      </Alert>

      <Group justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          Проблемных смен: <strong>{rows.length}</strong>
        </Text>
        <Group gap="xs" wrap="wrap">
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            onClick={() => void load()}
            loading={loading}
          >
            Обновить
          </Button>
          <Button
            size="xs"
            variant="light"
            onClick={() => void runRepair({ dryRun: true })}
            loading={repairing}
            disabled={!rows.length}
          >
            Проверить исправление
          </Button>
          <Button
            size="xs"
            color="orange"
            onClick={() => void runRepair({ dryRun: false })}
            loading={repairing}
            disabled={!rows.length}
          >
            Исправить все
          </Button>
        </Group>
      </Group>

      {lastResult && !lastResult.dry_run ? (
        <Text size="xs" c="dimmed">
          Последнее исправление: закрытые смены — {lastResult.fixed_closed}, открытые —{' '}
          {lastResult.fixed_open}. Осталось — {lastResult.remaining_total}.
        </Text>
      ) : null}

      {loading ? <PageCenterLoader label="Загрузка" minHeight={160} /> : null}

      {!loading && rows.length === 0 ? (
        <EmptyState
          title="Проблем не найдено"
          description="Зависших перерывов в сменах workspace нет."
        />
      ) : null}

      {!loading && rows.length > 0 ? (
        <DataTableScroll>
          <DataTable>
            <DataThead>
              <DataTr>
                <DataTh>Сотрудник</DataTh>
                <DataTh>Проблема</DataTh>
                <DataTh>Смена</DataTh>
                <DataTh>Перерыв с</DataTh>
                <DataTh variantNarrow>Длит.</DataTh>
                <DataTh variantNarrow>Лимит</DataTh>
                <DataTh />
              </DataTr>
            </DataThead>
            <DataTbody>
              {rows.map((row) => {
                const overLimit =
                  row.issue === 'open_shift_over_limit' &&
                  row.active_break_minutes > row.max_break_minutes
                return (
                  <DataTr
                    key={row.shift_log_id}
                    style={{
                      background: overLimit ? 'rgba(200, 40, 30, 0.08)' : undefined,
                    }}
                  >
                    <DataTd clip>{userLabel(row)}</DataTd>
                    <DataTd>
                      <Badge size="sm" variant="light" color={row.shift_open ? 'orange' : 'red'}>
                        {ISSUE_LABELS[row.issue] || row.issue}
                      </Badge>
                      {row.shift_open ? (
                        <Text size="xs" c="dimmed" mt={4}>
                          смена открыта
                        </Text>
                      ) : null}
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{formatDateTimeDot(row.shift_started_at)}</Text>
                      {row.shift_ended_at ? (
                        <Text size="xs" c="dimmed">
                          → {formatDateTimeDot(row.shift_ended_at)}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          → сейчас
                        </Text>
                      )}
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{formatDateTimeDot(row.break_started_at)}</Text>
                    </DataTd>
                    <DataTd style={{ textAlign: 'center' }}>
                      {row.active_break_minutes} мин
                    </DataTd>
                    <DataTd style={{ textAlign: 'center' }}>
                      {row.completed_break_minutes_today}/{row.max_break_minutes}
                    </DataTd>
                    <DataTd>
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="orange"
                        loading={repairingId === row.shift_log_id}
                        disabled={repairing && repairingId !== row.shift_log_id}
                        onClick={() =>
                          void runRepair({
                            dryRun: false,
                            shiftLogIds: [row.shift_log_id],
                          })
                        }
                      >
                        Исправить
                      </Button>
                    </DataTd>
                  </DataTr>
                )
              })}
            </DataTbody>
          </DataTable>
        </DataTableScroll>
      ) : null}
    </Stack>
  )
}
